import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { apiFetch } from '@/lib/api/rawFetch'
import type { SyncAcceptedResponse, SyncStatusResponse } from '@/lib/api/session11Types'

export function useSyncInventory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (mode: 'full' | 'delta' = 'delta') =>
      apiFetch<SyncAcceptedResponse>('POST', '/api/inventory/sync', { query: { mode } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sync-status'] })
    },
  })
}

function fetchSyncStatus(logId: number | null) {
  return apiFetch<SyncStatusResponse>('GET', '/api/inventory/sync-status', {
    query: logId ? { log_id: logId } : {},
  })
}

export function isFinishedSync(s: SyncStatusResponse | undefined): boolean {
  return s?.status === 'success' || s?.status === 'failed'
}

/** Polls one sync-log row every 4s until it reaches success/failed. */
export function useSyncStatus(logId: number | null, enabled: boolean) {
  return useQuery({
    queryKey: ['sync-status', logId] as const,
    queryFn: () => fetchSyncStatus(logId),
    enabled,
    refetchInterval: (query) => (enabled && !isFinishedSync(query.state.data) ? 4_000 : false),
    staleTime: 0,
  })
}

/** Latest sync-log row. Fetched on mount and again whenever ['sync-status']
 *  is invalidated (a sync starts or finishes); never polls on its own. */
export function useLatestSyncStatus() {
  return useQuery({
    queryKey: ['sync-status', null] as const,
    queryFn: () => fetchSyncStatus(null),
    refetchInterval: false,
    staleTime: 0,
  })
}

/** Queries that read the tables a Shopify sync rewrites (variants, inventory
 *  levels, sales windows, locations). */
export const SYNCED_DATA_QUERY_KEYS = [
  ['dashboard'],
  ['product-type-demand'],
  ['products'],
  ['inventory-locations'],
  ['no-barcode'],
  ['variant-inventory'],
  ['inventory-search'],
] as const

export function invalidateSyncedData(qc: QueryClient) {
  for (const queryKey of SYNCED_DATA_QUERY_KEYS) qc.invalidateQueries({ queryKey })
}
